<script setup lang="ts">
import MaterialCounts from "@/components/MaterialCounts.vue";
import type { Author } from "@/services/fobizzPlatformApi";
import { AppBookmarkableEnum, Collection } from "@/services/fobizzToolsApi";

const props = defineProps<{
  collection: Collection;
}>();

const userAvatar: ComputedRef<Author> = computed(() => {
  if (!props.collection.user)
    return { name: "", imageUrl: "" } as unknown as Author;
  const name = props.collection.user.publicDisplayName;
  const imageUrl =
    props.collection.user.imageUrl !== null
      ? props.collection.user.imageUrl
      : undefined;
  return { name, imageUrl } as unknown as Author;
});

defineEmits(["share", "like"]);

const { t } = useI18n();

const emailData = computed(() => ({
  subject: props.collection.name,
  body: t("collections.share.email.body", {
    name: props.collection.name,
    shareUrl: props.collection.shareUrl,
  }),
}));
</script>

<template>
  <div class="collection-header container row g-0">
    <div class="collection-header__image-wrapper col-12 col-md-4">
      <img
        :src="collection.image?.url"
        :alt="collection.name"
        class="collection-header__image img-fluid"
      />
    </div>

    <div class="collection-header__text col-12 col-md-8">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <span class="d-flex align-items-center">
          <CourseAvatar :author="userAvatar"></CourseAvatar>
          <span class="fw-semibold fw-semibold ms-2">{{
            collection.user?.publicDisplayName ||
            t("collections.labels.fobizzTeam")
          }}</span>
        </span>

        <div class="d-flex">
          <CollectionsShareDropdown
            button-id="shareButton"
            icon-name="upload"
            :email-subject="emailData.subject"
            :email-body="emailData.body"
          />
          <MaterialCounts
            :kind="AppBookmarkableEnum.COLLECTION"
            :material-id="collection.id"
            :author-id="collection.userId"
            :bookmarks-count="collection.bookmarksCount"
            :show-bookmarks-count="false"
            :name="collection.name || ''"
          />
        </div>
      </div>
      <h4>{{ collection.name }}</h4>
      <p class="flex-grow-1 mb-0">{{ collection.description }}</p>
    </div>
  </div>
</template>

<style scoped lang="scss">
.collection-header {
  display: flex;
  padding: 0;
  flex-wrap: wrap;

  @include media-breakpoint-down(md) {
    flex-direction: column;
  }

  &__image-wrapper {
    margin-bottom: $spacer-2;

    @include media-breakpoint-down(md) {
      margin-bottom: $spacer-4;
    }
  }

  &__image {
    width: 100%;
    height: 12.75rem;
    object-fit: cover;
  }

  &__text {
    display: flex;
    flex-direction: column;
    padding-left: $spacer-4;

    @include media-breakpoint-down(md) {
      padding-left: 0;
      margin-bottom: $spacer-4;
    }
  }

  &__bookmark {
    color: $color-black;

    &--fill {
      color: $brand-3;
    }
  }
}
</style>
